import { useFrame, useLoader, useThree, createPortal } from '@react-three/fiber'
import { useEffect, useMemo, useRef, useState } from 'react'
import { GLTFLoader, DRACOLoader } from "three/examples/jsm/Addons.js"
import { useAnimations, useKeyboardControls } from '@react-three/drei'
import * as THREE from 'three'
import { RigidBody, CapsuleCollider } from '@react-three/rapier'
import { useControls } from 'leva'

import {
  isMovementBlocked, touchMove, useStore, watchOpenStore, vicinoCassaStore,
  registraBraccio,
} from '../../store/uiStore'
import { cfgCassa } from '../../config/cassa'
import { DEBUG_MODE } from '../../config/debug'
import { getRig, aggiornaCamera, cfgCamera } from './cameraShake'
import OrologioFaccia, { cfgFaccia } from './OrologioFaccia'
import { preparaBraccio, applicaScalaMano } from './presaInMano'
import { VOCI, VEL_FAM, VEL_DIR, DIREZIONI } from '../../config/locomozione'

const SU = new THREE.Vector3(0, 1, 0)
// ============================================================
// L'OCCHIO STA SULLA TESTA, NON SOPRA LA TESTA.
// Prima la camera stava a un'altezza fissa sulla capsula e il CORPO pendeva
// dalla camera. L'occhio finiva 0.25 sopra l'osso Head e 0.13 davanti, cioe'
// appena FUORI dal cranio, e guardando in basso si vedevano nuca e spalle
// (segnalato tre volte: di lato, all'indietro, accovacciati). Per rimediare
// ogni clip portava la posizione media della propria testa, misurata in
// Blender, piu' una manciata di costanti per l'accovacciata: una tabella da
// rifare a ogni clip nuova, ed era sbagliata appena la clip non era una di
// quelle misurate.
//
// ORA L'INVARIANTE E' UNO SOLO: l'occhio sta a distanza FISSA dall'osso Head,
// in qualunque posa. Chi si muove per rispettarlo dipende dall'asse, e non e'
// una finezza:
//  * IN VERTICALE SI MUOVE LA CAMERA. I piedi devono restare a terra, quindi
//    accovacciandosi non si puo' alzare il corpo: e' la vista che scende, di
//    quanto scende davvero la testa. (Prima: la costante CALO_GIU 1.00, piu'
//    TESTA_ALTA_GIU 2.478 per la camminata accovacciata, che e' meno profonda.)
//  * IN ORIZZONTALE SI MUOVE IL CORPO. La vista non deve slittare: misurate in
//    Blender, le clip portano la testa 0.19 avanti di corsa, 0.23 di lato nella
//    falcata laterale e 0.60 avanti accovacciati, con 0.38 di oscillazione a
//    ogni passo. Copiare quei numeri nella camera sarebbe un beccheggio
//    continuo; il corpo invece si sposta del contrario e resta lui sotto
//    l'occhio, senza che si veda (di se' stessi non si vede la posizione).
//
// La misura la da' l'OSSO, a ogni fotogramma. Nessuna clip ha piu' numeri
// suoi: aggiungerne una non richiede di misurare niente.
// ============================================================
// dove sta il modello rispetto al centro della capsula (i piedi a terra):
// e' il vecchio -3.6 sotto l'occhio piu' i 2.6 dell'occhio sulla capsula.
const ALTEZZA_CORPO = -1.0
// l'occhio sopra l'osso Head, e quanto la testa gli resta DIETRO. Sono i due
// numeri della posa in piedi di prima (0.249 e 0.126, ricavati dai vecchi
// offset altezza -3.6 / avanti +0.7): da fermi l'inquadratura e' quella di
// sempre, e con essa la posa dell'orologio, che su quella distanza e'
// costruita.
// L'OCCHIO STA SOPRA L'OSSO Head, e piuttosto in alto: 0.45. Non e' un
// capriccio — e' quello che decide QUANTO CORPO si vede guardando in basso, ed
// e' stato misurato (percentuale di schermo coperta dal corpo a 85 gradi in
// giu', con la sola testa nascosta): 0.25 -> circa meta' schermo di spalle,
// 0.35 -> 45%, 0.45 -> 30%, 0.65 -> 21%, 0.8 -> 15%. A 0.45 la camera e'
// risultata "troppo sopra il corpo" (segnalato, e lo diceva anche l'orologio
// finito piu' in basso di prima): 0.35 e' il compromesso, col petto in vista
// e il quadrante quasi dov'era.
// IL PREZZO LO PAGA L'OROLOGIO, che sta sul braccio e quindi scende con il
// corpo: il quadrante passa da 18.5 a 42.3 gradi sotto l'orizzonte e da 0.36 a
// 0.46 dall'occhio. Per questo alzando il polso la visuale ora si inclina in
// giu' (cfgFaccia.inclinazione, -24 gradi) invece di raddrizzarsi: si guarda
// l'orologio come lo si guarda davvero. Alzando ancora l'occhio va rivisto
// quell'angolo, e oltre i 0.65 il quadrante diventa troppo piccolo per
// centrarne le icone.
const OCCHIO_SU = 0.35
const OCCHIO_DIETRO = 0.13
// Ripiego se un giorno il modello non avesse l'osso Head: l'altezza a cui
// sta in piedi (misurata sulla clip Idle). Non dovrebbe mai servire.
const ALTEZZA_TESTA = 3.35
// Quale mano tiene il prodotto: la SINISTRA, perche' la vetrina lo mette a
// sinistra della visuale. (L'orologio sta sullo stesso polso: tenendo un
// prodotto lo si vede al polso della mano che tiene, che e' giusto cosi'; col
// polso alzato la presa si spegne, vedi ProductShowcase.)
const LATO_PRESA = 'L'

// QUALI OSSA NON SI DISEGNANO IN PRIMA PERSONA: solo la TESTA, perche' la
// camera le sta dentro e si vedrebbe la nuca. Collo, petto e spalle si vedono
// (chiesto: "come prima che si vedeva un po' di collo/petto"), e si vedono
// DALL'ALTO invece che da dentro perche' l'occhio e' stato alzato — vedi
// OCCHIO_SU. Tagliando fino al petto, com'era prima, il tronco restava aperto
// e guardando in basso se ne vedeva l'interno: una conca scura in mezzo allo
// schermo. In ?debug si prova un altro elenco al volo:
// window.__corpo.nascondi([...]).
const NASCOSTE = ['Head']


// TEMPI DELLE DISSOLVENZE DEL MOVIMENTO (costanti di tempo, secondi):
// partire/fermarsi, cambiare direzione, passare alla corsa.
const TAU_MOTO = 0.12
// istante della prima chiave di ogni clip (le AnimationClip qui non hanno userData)
const INIZIO_CLIP = new WeakMap()
const TAU_DIREZIONE = 0.12
const TAU_CORSA = 0.18


// IL CORPO SEGUE LA VISUALE CON UN RITARDO. Prima era inchiodato allo yaw
// della camera: girando la testa di scatto il torso girava nello stesso
// istante, come un pupazzo su un perno. Ora insegue con una costante di
// tempo (piu' svelto quando si cammina, perche' camminando le spalle si
// allineano alla direzione) e non resta mai indietro di piu' di un tot — oltre,
// viene trascinato. Valori di partenza; si tarano da Leva, "Corpo".
const RITARDO_CORPO = 0.18      // secondi (costante di tempo); 0 = niente ritardo
const RITARDO_CORPO_MOTO = 0.45 // fattore sulla costante quando ci si muove
// SOPRA QUANTI GRADI il corpo si decide a girare. Sotto, non si muove di un
// grado: gira solo la testa, che e' come si guarda uno scaffale restando
// piantati. Superata la soglia il corpo si RIALLINEA tutto, come chi sposta i
// piedi, e poi torna fermo. Camminando la soglia si annulla da sola.
const SOGLIA_CORPO = 40        // gradi

// I numeri del corpo stanno in un oggetto di MODULO e gli slider Leva ci
// scrivono dentro solo quando cambiano DAVVERO. E' la stessa regola di
// cfgCassa e cfgVita, e serve a poter tarare da console (window.__corpo in
// ?debug) senza ricompilare: riassegnando l'oggetto a ogni render, un valore
// scritto a mano durerebbe un fotogramma.
export const cfgCorpo = {
  altezza: ALTEZZA_CORPO,
  occhioSu: OCCHIO_SU,
  occhioDietro: OCCHIO_DIETRO,
  ritardoCorpo: RITARDO_CORPO,
  ritardoCorpoMoto: RITARDO_CORPO_MOTO,
  sogliaCorpo: SOGLIA_CORPO,
}

// Scratch a livello di modulo: dentro al loop non si alloca NULLA.
// Prima ogni frame nascevano un Vector3, un Quaternion e un Euler (più
// i vettori ricreati a ogni render di React): a 60-120 fps è spazzatura
// continua, e le pause del garbage collector sono esattamente gli
// scatti saltuari che si vedevano guardandosi il corpo.
const tmp = {
  velocita: new THREE.Vector3(),
  direzione: new THREE.Vector3(),
  avanti: new THREE.Vector3(),
  lato: new THREE.Vector3(),
  sguardo: new THREE.Vector3(),
  destra: new THREE.Vector3(),
  bersaglio: new THREE.Vector3(),
  eulYaw: new THREE.Euler(),
  eulMira: new THREE.Euler(),
  attesa: new THREE.Vector3(),   // dove porta la testa la miscela di clip in corso
}

// ============================================================
// DOVE PORTA LA TESTA CIASCUNA CLIP, misurato a runtime sui dati
// dell'animazione: niente mixer, niente clone, non si tocca niente di quello
// che si vede. Si valutano solo le tracce delle sette ossa fra la radice del
// modello e la testa, campionando il ciclo.
//
// PERCHE' NON SI LEGGE PIU' L'OSSO A OGNI FOTOGRAMMA. Prima si leggeva la
// testa vera e la si filtrava. Ma la testa vera OSCILLA: 0.38 unita' avanti e
// indietro a ogni passo accovacciati (misurate su CrouchWalk), 0.14 in
// verticale camminando. Il filtro quindi non poteva vincere:
//  * lento (0.3 s, com'era) -> accovacciandosi l'occhio restava 1.06 sopra la
//    testa invece di 0.45, e rialzandosi SCENDEVA A -0.12, cioe' dentro il
//    modello, e ci metteva quasi due secondi a rientrare (segnalato);
//    partendo a camminare accovacciati la testa finiva 0.27 DAVANTI
//    all'occhio — la schiena in faccia — e poi oscillava di +-0.3.
//  * veloce -> il corpo sobbalzerebbe a ogni passo.
// La MEDIA della clip non ha nessuno dei due difetti: e' un numero fisso per
// clip, l'oscillazione non c'e' per costruzione, e i pesi con cui le clip si
// mescolano cambiano gia' in modo continuo (TAU_MOTO, TAU_DIREZIONE). Il
// risultato e' istantaneo e liscio insieme. E' la vecchia tabella `testa` di
// config/locomozione.js, ma misurata dal programma invece che a mano in
// Blender: aggiungere una clip non richiede di misurare niente.
// ============================================================
const tmpMisura = {
  m: new THREE.Matrix4(),
  acc: new THREE.Matrix4(),
  pos: new THREE.Vector3(),
  qua: new THREE.Quaternion(),
  scl: new THREE.Vector3(),
  punto: new THREE.Vector3(),
}

function catenaOssa(osso, radice) {
  const c = []
  for (let n = osso; n && n !== radice; n = n.parent) c.push(n)
  return c.reverse()   // dalla radice all'osso
}

// Posizione della testa a un dato istante della clip, nel frame del corpo.
// Le ossa senza traccia in questa clip tengono il valore che hanno adesso, che
// per loro e' quello di riposo: i canali che nessuna clip anima sono stati
// tolti dal GLB proprio perche' costanti e uguali al riposo.
function testaAllIstante(interpolanti, catena, qModello, t, fuori) {
  const { m, acc, pos, qua, scl } = tmpMisura
  acc.identity()
  for (const osso of catena) {
    const leggi = (prop, out) => {
      const it = interpolanti.get(`${osso.name}.${prop}`)
      return it ? out.fromArray(it.evaluate(t)) : out.copy(osso[prop])
    }
    leggi('position', pos)
    leggi('quaternion', qua)
    leggi('scale', scl)
    m.compose(pos, qua, scl)
    acc.multiply(m)
  }
  return fuori.setFromMatrixPosition(acc).applyQuaternion(qModello)
}

function misuraClip(clip, catena, qModello, campioni = 24) {
  const interpolanti = new Map()
  for (const t of clip.tracks) interpolanti.set(t.name, t.createInterpolant())
  const inizio = Math.min(...clip.tracks.map((t) => t.times[0]))
  const dur = clip.duration - inizio
  const media = new THREE.Vector3()
  for (let i = 0; i < campioni; i++) {
    media.add(testaAllIstante(interpolanti, catena, qModello,
      inizio + (dur * i) / campioni, tmpMisura.punto))
  }
  media.divideScalar(campioni)
  // Per le clip ADDITIVE serve invece lo scarto fra l'ultimo fotogramma e il
  // primo: il primo e' la posa di riferimento che makeClipAdditive sottrae,
  // quindi quello scarto e' quanto la clip sposta la testa quando e' a peso 1.
  const fine = testaAllIstante(interpolanti, catena, qModello, clip.duration, new THREE.Vector3())
  const scarto = fine.sub(testaAllIstante(interpolanti, catena, qModello, inizio, tmpMisura.punto))
  return { media, scarto }
}

// Posa il piano della UI sulla faccia del quadrante. Il VERSO degli
// assi si decide guardando la camera — la normale deve puntare verso
// l'occhio e la cima dello schermo verso l'alto — così non serve
// sapere come la conversione Y-up di glTF ha girato gli assi locali
// della mesh esportata. Gira una volta per ogni alzata, non per frame.
function orientaFaccia(mesh, faccia, camera) {
  faccia.nodo.updateWorldMatrix(true, false)
  const inv = new THREE.Matrix4().copy(faccia.nodo.matrixWorld).invert()
  const origine = new THREE.Vector3().applyMatrix4(inv)
  const qCam = camera.getWorldQuaternion(new THREE.Quaternion())
  const versoLoc = (v) => v.applyQuaternion(qCam).applyMatrix4(inv).sub(origine).normalize()
  const destra = versoLoc(new THREE.Vector3(1, 0, 0))
  const su = versoLoc(new THREE.Vector3(0, 1, 0))
  const camLoc = new THREE.Vector3()
    .setFromMatrixPosition(camera.matrixWorld).applyMatrix4(inv)

  const z = faccia.normale.clone()
  if (z.dot(camLoc.sub(faccia.centro)) < 0) z.negate()

  // L'asse X della UI è lo spigolo che sullo schermo corre ORIZZONTALE,
  // NON il più lungo: quale dei due lo sia dipende da come l'esportatore
  // ha orientato il quad, e tirare a indovinare costa un quadrante
  // ruotato di 90 gradi (successo).
  const primo = Math.abs(faccia.a.dot(destra)) >= Math.abs(faccia.b.dot(destra))
  const x = (primo ? faccia.a : faccia.b).clone()
  const lungoX = primo ? faccia.lungA : faccia.lungB
  const lungoY = primo ? faccia.lungB : faccia.lungA
  if (x.dot(destra) < 0) x.negate()
  x.addScaledVector(z, -x.dot(z)).normalize()
  const y = new THREE.Vector3().crossVectors(z, x)
  if (y.dot(su) < 0) { x.negate(); y.negate() }

  // Il piano copre TUTTO il quad, moltiplicato per il riempimento che lo
  // porta fino ai bordi della cassa. Niente iscrizione a proporzioni
  // fisse: è il canvas ad adattarsi a questa forma (vedi OrologioFaccia),
  // altrimenti resterebbero due bande vuote ai lati.
  const r = cfgFaccia.riempimento
  mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z))
  // un soffio sopra la faccia, altrimenti le due superfici combattono
  mesh.position.copy(faccia.centro).addScaledVector(z, Math.min(lungoX, lungoY) * 0.03)
  mesh.scale.set(lungoX * r, lungoY * r, 1)
  mesh.userData.riemp = r
}

// LA CURVA DI DISCESA LA DA' LA CLIP, non un conto a parte. Nella clip
// additiva la traccia di posizione dell'osso Body E' GIA' lo scostamento
// dalla posa in piedi (makeClipAdditive sottrae il primo fotogramma):
// normalizzandola sul valore finale si ottiene "quanto sono sceso", da 0 a
// 1 — oltre-elongazione del 6% compresa.
// SERVE DAVVERO: con una rampa lineare sul tempo della clip, occhi e corpo
// si sfasavano. Misurato in gioco a meta' gesto: la testa era gia' scesa
// del 78% e gli occhi del 46% — per un paio di decimi le spalle salivano
// dentro l'inquadratura.
function curvaDiscesa(clip) {
  const tr = clip.tracks.find((t) => /Body\.position$/.test(t.name))
  if (!tr || tr.times.length < 2) return null
  const ultimo = tr.values[(tr.times.length - 1) * 3 + 1]
  if (!ultimo) return null
  return {
    tempi: tr.times,
    frazione: Array.from(tr.times, (_, i) => tr.values[i * 3 + 1] / ultimo),
  }
}

function campiona(curva, t) {
  const { tempi, frazione } = curva
  if (t <= tempi[0]) return frazione[0]
  for (let i = 1; i < tempi.length; i++) {
    if (t <= tempi[i]) {
      const passo = tempi[i] - tempi[i - 1]
      const k = passo > 0 ? (t - tempi[i - 1]) / passo : 0
      return frazione[i - 1] + (frazione[i] - frazione[i - 1]) * k
    }
  }
  return frazione[frazione.length - 1]
}

export default function Player(props)
{
  const body = useRef()   // solo il collisore: la fisica
  const corpo = useRef()  // il corpo VISIBILE, fuori dalla fisica
  const player = useRef()
  const [ , getKeys ] = useKeyboardControls()

  // In ?debug: window.__teletrasporta(x, z) mette il giocatore dove si
  // vuole, per guardare da vicino una cosa del negozio senza camminarci.
  // Si sposta il CORPO FISICO (la camera lo insegue da sola); la quota e'
  // quella di partenza, a terra.
  useEffect(() => {
    if (!DEBUG_MODE) return undefined
    window.__teletrasporta = (x, z, y = 1) => {
      body.current?.setTranslation({ x, y, z }, true)
      body.current?.setLinvel({ x: 0, y: 0, z: 0 }, true)
    }
    return () => { delete window.__teletrasporta }
  }, [])

  // Calibrazione del corpo e dell'occhio (solo ?debug). I primi tre cursori
  // sono l'invariante descritto in cima al file: dove sta il corpo sulla
  // capsula, e dove sta l'occhio rispetto all'osso Head.
  const levaCorpo = useControls('Corpo', {
    altezza:      { value: ALTEZZA_CORPO, min: -2, max: 0, step: 0.02, label: 'corpo sulla capsula' },
    occhioSu:     { value: OCCHIO_SU, min: -0.4, max: 0.8, step: 0.01, label: 'occhio sopra la testa' },
    occhioDietro: { value: OCCHIO_DIETRO, min: -0.5, max: 0.6, step: 0.01, label: 'testa dietro l\'occhio' },
    ritardoCorpo: { value: RITARDO_CORPO, min: 0, max: 0.8, step: 0.01, label: 'ritardo corpo (s)' },
    ritardoCorpoMoto: { value: RITARDO_CORPO_MOTO, min: 0.1, max: 1, step: 0.05, label: 'ritardo camminando (x)' },
    sogliaCorpo: { value: SOGLIA_CORPO, min: 0, max: 90, step: 1, label: 'soglia di rotazione (gradi)' },
  }, { collapsed: true })
  // gli slider si applicano solo quando cambiano davvero (vedi cfgCorpo)
  const levaPrec = useRef({})
  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const k in levaCorpo) {
      if (levaCorpo[k] !== levaPrec.current[k]) {
        cfgCorpo[k] = levaCorpo[k]
        levaPrec.current[k] = levaCorpo[k]
      }
    }
  }, [levaCorpo])

  const { scene, animations } =
  useLoader(GLTFLoader, 'models/playerModel-draco.glb', (loader) =>
    {
      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('draco/')
      loader.setDRACOLoader(dracoLoader)
    }
  )
  const { actions, mixer } = useAnimations(animations, player)

  // ------------------------------------------------------------
  // OROLOGIO: la clip "Watch" si riproduce in ADDITIVO.
  // Necessario, non un vezzo: in questo modello OGNI clip chiava
  // tutte e 62 le ossa, quindi in modalità normale a peso 1 il
  // mixer farebbe la MEDIA fra il braccio che cammina e quello
  // alzato, e le gambe si fermerebbero. In additivo il gesto si
  // somma sopra Walk, Run o Idle indifferentemente.
  // Il fotogramma 1 della clip è la posa di riposo: è esattamente
  // il riferimento che makeClipAdditive deve sottrarre.
  // ------------------------------------------------------------
  const azioneOrologio = useRef(null)
  useEffect(() => {
    const clip = animations.find((c) => c.name === 'Watch') ||
                 animations.find((c) => /watch/i.test(c.name))
    if (!clip || !mixer || !player.current) return
    const additiva = THREE.AnimationUtils.makeClipAdditive(clip.clone())
    additiva.name = 'WatchAdditive'
    const a = mixer.clipAction(additiva, player.current, THREE.AdditiveAnimationBlendMode)
    a.loop = THREE.LoopOnce
    a.clampWhenFinished = true   // resta alzato invece di tornare giù da solo
    a.play()
    a.paused = true
    a.time = 0
    azioneOrologio.current = a
    return () => {
      a.stop()
      mixer.uncacheAction(additiva, player.current)
      azioneOrologio.current = null
    }
  }, [animations, mixer])

  // ------------------------------------------------------------
  // ACCOVACCIATA (tasto Ctrl): stessa macchina dell'orologio, e per la
  // stessa ragione — ADDITIVA. Ogni clip di questo modello chiava tutte
  // e 62 le ossa: in modalita' normale il mixer farebbe la media fra il
  // camminare e lo stare giu', e verrebbe fuori una cosa a meta'. In
  // additivo il piegarsi delle gambe si SOMMA sopra Idle, Walk o Run,
  // quindi ci si puo' anche muovere da accovacciati.
  // Il fotogramma 1 della clip e' la posa di Idle, che e' esattamente il
  // riferimento che makeClipAdditive sottrae (vedi la lezione
  // sull'orologio: sbagliarlo manda il corpo da tutt'altra parte).
  // ------------------------------------------------------------
  const azioneGiu = useRef(null)
  const curvaGiu = useRef(null)   // quanto si e' scesi, letto dalla clip stessa
  useEffect(() => {
    const clip = animations.find((c) => c.name === 'Crouch')
    if (!clip || !mixer || !player.current) return undefined
    const additiva = THREE.AnimationUtils.makeClipAdditive(clip.clone())
    additiva.name = 'CrouchAdditive'
    const a = mixer.clipAction(additiva, player.current, THREE.AdditiveAnimationBlendMode)
    a.loop = THREE.LoopOnce
    a.clampWhenFinished = true   // resta giu' finche' si tiene premuto
    a.play()
    a.paused = true
    a.time = 0
    azioneGiu.current = a
    curvaGiu.current = curvaDiscesa(additiva)
    return () => {
      a.stop()
      mixer.uncacheAction(additiva, player.current)
      azioneGiu.current = null
      curvaGiu.current = null
    }
  }, [animations, mixer])
  const giuPrima = useRef(false)

  // ------------------------------------------------------------
  // IL BRACCIO CHE TIENE IL PRODOTTO.
  // Qui si prepara soltanto (ossa e lunghezze a riposo) e lo si registra: a
  // muoverlo e' ProductShowcase, che nello stesso fotogramma gira DOPO questo
  // componente, quindi la mano si mette dove il prodotto e' GIA' stato messo
  // invece di inseguirlo con un fotogramma di ritardo.
  // PRIMA C'ERA LA CLIP `Interact` del pacchetto (una mano che si allunga e
  // torna): all'utente non piaceva, e comunque non poteva funzionare — una
  // clip e' una posa fissa, mentre il prodotto si muove con la visuale e la
  // mano deve stargli dietro dovunque si guardi. Serve una cinematica
  // inversa, che sta in presaInMano.js.
  // ------------------------------------------------------------
  const braccio = useRef(null)
  useEffect(() => {
    if (!scene) return undefined
    braccio.current = preparaBraccio(scene, LATO_PRESA)
    registraBraccio(braccio.current)
    return () => { braccio.current = null; registraBraccio(null) }
  }, [scene])
  // Stato del movimento, tutto in un ref (cambia a ogni fotogramma, React non
  // deve saperne niente): quanto ci si muove, quanto si corre, i pesi delle
  // quattro direzioni e la FASE comune del passo.
  const loco = useRef({ moto: 0, corsa: 0, fase: 0, dir: { avanti: 1, indietro: 0, sinistra: 0, destra: 0 } })
  // In ?debug: window.__loco dice lo stato del movimento e il peso di ogni
  // clip — l'unico modo di controllare le miscele senza guardarsi i piedi.
  useEffect(() => {
    if (!DEBUG_MODE) return undefined
    window.__loco = () => {
      const pesi = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const n in actions) {
        const a = actions[n]
        if (a && a.getEffectiveWeight() > 0.001) pesi[n] = +a.getEffectiveWeight().toFixed(3)
      }
      return { ...loco.current, dir: { ...loco.current.dir }, pesi }
    }
    return () => { delete window.__loco }
  }, [actions])
  // Posizione della testa rispetto all'origine del modello (filtrata), e la
  // posizione data al modello nel fotogramma prima: le matrici che si leggono
  // sono quelle dell'ultimo disegno, fatto con QUELLA posizione.
  const ossoTesta = useMemo(() => scene.getObjectByName('Head'), [scene])

  // ------------------------------------------------------------
  // IN PRIMA PERSONA LA TESTA E LE SPALLE NON SI DISEGNANO.
  // La camera sta dentro la testa del personaggio, che e' grossa e tonda:
  // guardando in basso si finiva a vedere la propria nuca e la cupola delle
  // spalle, e camminando accovacciati — dove la schiena e' piegata in avanti
  // e sta proprio sotto l'occhio — riempiva mezzo schermo (segnalato tre
  // volte: laterali, indietro, accovacciato).
  // NON si risolve spostando il corpo: l'occhio sta al CENTRO della testa,
  // quindi qualunque correzione lascia il collo e le spalle attorno alla
  // camera. E' il rimedio dei giochi in prima persona: la parte alta del
  // corpo non viene disegnata affatto. Restano braccia, mani, orologio,
  // pancia, gambe e piedi, cioe' tutto quello che in prima persona si deve
  // vedere davvero.
  // COME: per ogni vertice si somma il peso che ha sulle ossa da nascondere
  // (attributo `nascondi`, calcolato UNA volta sulla CPU) e il frammento si
  // butta via se supera meta'. Un attributo e non una uniforme perche' cosi'
  // nello shader non c'e' nessun ciclo sulle ossa; le mesh senza l'attributo
  // leggono 0, cioe' restano visibili.
  // I materiali sono CONDIVISI fra le mesh del personaggio (Skin.001 sta su
  // corpo, gambe e piedi): si clonano, o la modifica finirebbe anche dove
  // l'attributo non esiste.
  // MISURATO nella build, corpo disegnato da solo e contati i pixel, vista a
  // 57 gradi in basso: da fermi copriva il 23% dello schermo, ora il 5,7%;
  // camminando accovacciati in avanti dal 26% a quasi niente (restano le
  // mani e il colletto). Con l'orologio alzato braccio e quadrante sono
  // interi: il taglio finisce alla spalla.
  // ------------------------------------------------------------
  useEffect(() => {
    const pelli = []
    scene.traverse((o) => { if (o.isSkinnedMesh) pelli.push(o) })
    if (!pelli.length) return undefined
    const ossa = pelli[0].skeleton.bones.map((b) => b.name)
    // Il peso di ogni vertice sulle ossa da nascondere. E' una funzione e non
    // un giro dritto perche' in ?debug si puo' richiamare con un altro elenco
    // (window.__corpo.nascondi([...])) e vedere subito il taglio che ne esce.
    const calcolaTaglio = (nomi) => {
      const quali = new Set(nomi.map((n) => ossa.indexOf(n)).filter((i) => i >= 0))
      for (const m of pelli) {
        const si = m.geometry.attributes.skinIndex
        const sw = m.geometry.attributes.skinWeight
        if (!si || !sw) continue
        let att = m.geometry.attributes.nascondi
        if (!att) {
          att = new THREE.BufferAttribute(new Float32Array(si.count), 1)
          m.geometry.setAttribute('nascondi', att)
        }
        for (let i = 0; i < si.count; i++) {
          let h = 0
          for (let j = 0; j < 4; j++) {
            if (quali.has(si.getComponent(i, j))) h += sw.getComponent(i, j)
          }
          att.array[i] = h
        }
        att.needsUpdate = true
      }
    }
    calcolaTaglio(NASCOSTE)
    const prima = []
    for (const m of pelli) {
      if (!m.geometry.attributes.nascondi) continue
      prima.push([m, m.material])
      const mat = m.material.clone()
      mat.onBeforeCompile = (sh) => {
        sh.vertexShader = 'attribute float nascondi;\nvarying float vNascondi;\n' +
          sh.vertexShader.replace('void main() {', 'void main() {\n  vNascondi = nascondi;')
        sh.fragmentShader = 'varying float vNascondi;\n' +
          sh.fragmentShader.replace('void main() {', 'void main() {\n  if (vNascondi > 0.5) discard;')
      }
      // senza una chiave propria three riuserebbe il programma gia' compilato
      // per lo stesso materiale e la modifica non arriverebbe mai alla GPU
      mat.customProgramCacheKey = () => 'momoz-prima-persona'
      mat.needsUpdate = true
      m.material = mat
    }
    // In ?debug: window.__taglioCorpo(false) rimette il corpo intero (serve a
    // rifare il confronto prima/dopo senza ricompilare).
    if (DEBUG_MODE) {
      // renderer e scena a portata di console: servono a fotografare un
      // dettaglio da una camera di servizio (per esempio la mano che tiene il
      // prodotto, che altrimenti si guarda solo di sbieco dalla prima persona)
      window.__gl = gl3d
      window.__scena = scena3d
      window.__corpo = cfgCorpo
      cfgCorpo.nascondi = calcolaTaglio
      const salvati = pelli.map((m) => [m, m.geometry.attributes.nascondi?.array.slice()])
      window.__taglioCorpo = (acceso = true) => {
        for (const [m, orig] of salvati) {
          const att = m.geometry.attributes.nascondi
          if (!att || !orig) continue
          att.array.set(acceso ? orig : new Float32Array(orig.length))
          att.needsUpdate = true
        }
      }
    }
    return () => {
      if (DEBUG_MODE) { delete window.__taglioCorpo; delete window.__corpo }
      for (const [m, mat] of prima) { m.material.dispose(); m.material = mat }
    }
  }, [scene])
  // posizione dell'osso Head nel riferimento del corpo, filtrata
  // Media della testa per ogni clip, misurata UNA volta al montaggio.
  const medieTesta = useRef(null)
  useEffect(() => {
    if (!ossoTesta || !player.current || !animations?.length) return
    const catena = catenaOssa(ossoTesta, player.current)
    const q = player.current.quaternion
    const tab = {}
    for (const clip of animations) {
      const { media, scarto } = misuraClip(clip, catena, q)
      tab[clip.name] = media
      // quanto scende (e arretra) la testa accovacciandosi del tutto
      if (clip.name === 'Crouch') tab.piega = scarto
    }
    medieTesta.current = tab
    if (DEBUG_MODE) window.__medieTesta = tab
  }, [animations, ossoTesta, scene])

  // il corpo e' gia' stato messo al suo posto almeno una volta?
  const corpoPosato = useRef(false)
  // yaw del corpo (null = non ancora letto) e se in questo momento si sta
  // riallineando alla visuale dopo aver superato la soglia
  const yawCorpo = useRef(null)
  const giraCorpo = useRef(false)

  // Apertura/chiusura: la stessa clip riprodotta avanti o indietro,
  // così chiudere a metà salita riparte da dove sta il braccio.
  const orologioAperto = useStore(watchOpenStore)
  const ritmoBase = useRef(1)
  const pitchSalvato = useRef(0)
  const ripristino = useRef(false)
  const ripristinoDa = useRef(0)
  const ultimoInput = useRef(0)

  // Il ritorno della visuale deve CEDERE il passo al giocatore: alla
  // chiusura il pointer lock è già riagganciato, quindi mouse e
  // animazione tirerebbero in direzioni opposte e si troverebbe un punto
  // di equilibrio in cui la visuale sembra bloccata — segnalato.
  // Qui si annota SOLO il momento dell'ultimo input; a decidere è il
  // useFrame confrontando i due istanti. Legarlo allo stato "ripristino
  // in corso" non funzionava: se l'input arriva prima che il frame
  // successivo se ne accorga, l'annullamento andava perso.
  // Apertura e chiusura si annotano QUI, non dentro useFrame: l'effetto
  // parte insieme al cambio di stato, mentre il frame successivo può
  // arrivare molto dopo (scheda in secondo piano, rAF strozzato) e
  // registrerebbe un istante posteriore all'input del giocatore,
  // mandando all'aria il confronto fra i due tempi.
  const camera3d = useThree((st3) => st3.camera)
  const gl3d = useThree((st3) => st3.gl)
  const scena3d = useThree((st3) => st3.scene)
  // In ?debug: window.__vistaCorpo() dice QUANTO DEL PROPRIO CORPO SI VEDE:
  // ridisegna il solo corpo dalla camera di gioco in una tela piccola e conta
  // i pixel coperti (frazione dello schermo). Misura cio' che vede il
  // giocatore, non la posizione di un osso.
  useEffect(() => {
    if (!DEBUG_MODE) return undefined
    const W = 160, H = 100
    const rt = new THREE.WebGLRenderTarget(W, H)
    const buf = new Uint8Array(W * H * 4)
    window.__vistaCorpo = () => {
      if (!corpo.current) return null
      const cam = camera3d
      const strati = cam.layers.mask
      const toccati = []
      corpo.current.traverse((o) => { if (o.isMesh) { toccati.push([o, o.layers.mask]); o.layers.enable(7) } })
      cam.layers.set(7)
      const sfondo = scena3d.background
      scena3d.background = null
      const prima = gl3d.getRenderTarget()
      const colore = new THREE.Color(); gl3d.getClearColor(colore)
      const alfa = gl3d.getClearAlpha()
      gl3d.setRenderTarget(rt)
      gl3d.setClearColor(0x000000, 0)
      gl3d.clear()
      gl3d.render(scena3d, cam)
      gl3d.readRenderTargetPixels(rt, 0, 0, W, H, buf)
      gl3d.setRenderTarget(prima)
      gl3d.setClearColor(colore, alfa)
      scena3d.background = sfondo
      cam.layers.mask = strati
      for (const [o, m] of toccati) o.layers.mask = m
      let tot = 0, alto = 0
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        if (buf[(y * W + x) * 4 + 3] > 0) { tot++; if (y > H * 0.25) alto++ }
      }
      return { copre: +(tot / (W * H)).toFixed(4), sopraIlQuarto: +(alto / (W * H)).toFixed(4) , buf, W, H }
    }
    return () => { delete window.__vistaCorpo; rt.dispose() }
  }, [camera3d, gl3d, scena3d])
  const primoGiro = useRef(true)
  useEffect(() => {
    if (primoGiro.current) { primoGiro.current = false; return }
    const d = new THREE.Vector3(0, 0, 1).applyQuaternion(camera3d.quaternion)
    const pitch = Math.asin(THREE.MathUtils.clamp(-d.y, -1, 1))
    if (orologioAperto) {
      pitchSalvato.current = pitch
      ripristino.current = false
    } else {
      ripristino.current = true
      ripristinoDa.current = performance.now()
    }
  }, [orologioAperto, camera3d])

  useEffect(() => {
    // Si annotano solo i movimenti VERI. Nei ~150 ms dopo l'aggancio del
    // pointer lock la sensibilità è a zero (CameraController): quei
    // mousemove non muovono niente, e contarli come input del giocatore
    // faceva rinunciare al ritorno prima ancora di cominciarlo.
    const segna = () => {
      if (performance.now() < cfgCamera.sordinaFino) return
      ultimoInput.current = performance.now()
    }
    window.addEventListener('mousemove', segna)
    window.addEventListener('touchmove', segna)
    return () => {
      window.removeEventListener('mousemove', segna)
      window.removeEventListener('touchmove', segna)
    }
  }, [])
  useEffect(() => {
    const a = azioneOrologio.current
    if (!a) return
    if (!orologioAperto && a.time === 0) return   // già giù al montaggio
    a.enabled = true
    a.paused = false
    a.timeScale = orologioAperto ? 1 : -1
    if (!a.isRunning()) a.play()
  }, [orologioAperto])

  // Dal quad WatchScreen ricavo centro, spigoli e normale IN SPAZIO
  // LOCALE: sono i dati con cui si posa il piano della UI.
  const [faccia, setFaccia] = useState(null)
  const facciaRef = useRef(null)
  const orientata = useRef(false)
  useEffect(() => {
    const s = scene.getObjectByName('WatchScreen')
    const pos = s?.geometry?.attributes?.position
    if (!pos) { setFaccia(null); return }
    // Draco può riordinare (e duplicare) i vertici: la soglia è 1e-6 sul
    // quadrato della distanza, cioè 1e-3 unità — sopra l'errore di
    // quantizzazione a 14 bit e ben sotto il lato del quad.
    const unici = []
    for (let i = 0; i < pos.count; i++) {
      const v = new THREE.Vector3().fromBufferAttribute(pos, i)
      if (!unici.some((u) => u.distanceToSquared(v) < 1e-6)) unici.push(v)
    }
    if (unici.length !== 4) { setFaccia(null); return }
    // i due spigoli sono i vicini del primo vertice, il quarto è la diagonale
    const vicini = [1, 2, 3].sort(
      (a, b) => unici[0].distanceTo(unici[a]) - unici[0].distanceTo(unici[b]))
    const e1 = unici[vicini[0]].clone().sub(unici[0])
    const e2 = unici[vicini[1]].clone().sub(unici[0])
    setFaccia({
      nodo: s,
      centro: unici[0].clone().add(e1.clone().add(e2).multiplyScalar(0.5)),
      a: e1.clone().normalize(),
      b: e2.clone().normalize(),
      lungA: e1.length(),
      lungB: e2.length(),
      normale: new THREE.Vector3().crossVectors(e1, e2).normalize(),
    })
  }, [scene])

  // Le rotazioni si bloccano UNA VOLTA: prima si richiamava
  // lockRotations a ogni frame (una chiamata nel wasm per nulla)
  useEffect(() => { body.current?.lockRotations(true, true) }, [])

  useFrame((state, delta) => {
    // LA MANO E' PIU' PICCOLA DEL MODELLO. Va riapplicato a ogni fotogramma
    // perche' una clip anima anche la scala del polso e il mixer, che gira
    // PRIMA di questo useFrame (useAnimations e' montato sopra), la
    // riscrive. Sta qui e non in ProductShowcase perche' la mano dev'essere
    // della sua misura anche quando non si tiene niente in mano.
    applicaScalaMano(braccio.current)
    // Solo il carrello aperto ferma il personaggio: con la pagina
    // prodotto si continua a camminare (WASD attivi, visuale ferma)
    const uiOpen = isMovementBlocked()
    const keys = getKeys()
    // Joystick touch: intensità 0..1; spinto quasi a fondo = corsa
    const joyMag = uiOpen ? 0 : Math.min(1, Math.hypot(touchMove.x, touchMove.y))
    const joyActive = joyMag > 0.05
    const forward  = !uiOpen && keys.forward
    const backward = !uiOpen && keys.backward
    const left     = !uiOpen && keys.left
    const right    = !uiOpen && keys.right
    const giu      = !uiOpen && keys.giu
    const shift    = !uiOpen && !giu && (keys.shift || joyMag > 0.9)
    const inMovimento = joyActive || forward || backward || left || right

    // --- ACCOVACCIATA ---
    // Il gesto lo fa la clip additiva, avanti o indietro come l'orologio:
    // lasciando Ctrl a meta' discesa si risale da dove si era arrivati.
    // IL CALO DELLA VISUALE LO DA' LA CLIP STESSA (curvaDiscesa, in cima al
    // file): occhi e corpo devono scendere insieme, o per un paio di decimi
    // le spalle salgono dentro l'inquadratura.
    const azGiu = azioneGiu.current
    if (azGiu && giu !== giuPrima.current) {
      giuPrima.current = giu
      azGiu.enabled = true
      azGiu.paused = false
      azGiu.timeScale = giu ? 1 : -1
      if (!azGiu.isRunning()) azGiu.play()
    }

    // ============================================================
    // MOVIMENTO IN TUTTE LE DIREZIONI (vedi config/locomozione.js).
    // Ogni direzione ha la sua clip, in piedi, di corsa e accovacciati, e si
    // MESCOLANO: una diagonale e' meta' avanti e meta' di lato. Prima c'era
    // una clip sola (Walk) qualunque tasto si premesse: di lato e all'indietro
    // si vedeva il passo in avanti che scivolava di traverso (segnalato).
    // ============================================================
    const L = loco.current
    // direzione chiesta, nel riferimento della visuale: x = destra, y = avanti
    const mx = joyActive ? touchMove.x : Number(right) - Number(left)
    const my = joyActive ? -touchMove.y : Number(forward) - Number(backward)
    const somma = Math.abs(mx) + Math.abs(my)
    const kDir = 1 - Math.exp(-delta / TAU_DIREZIONE)
    if (somma > 1e-3) {
      const mira = { avanti: Math.max(my, 0) / somma, indietro: Math.max(-my, 0) / somma,
                     sinistra: Math.max(-mx, 0) / somma, destra: Math.max(mx, 0) / somma }
      for (const d of DIREZIONI) L.dir[d] += (mira[d] - L.dir[d]) * kDir
    }
    L.moto += ((inMovimento ? 1 : 0) - L.moto) * (1 - Math.exp(-delta / TAU_MOTO))
    L.corsa += ((shift ? 1 : 0) - L.corsa) * (1 - Math.exp(-delta / TAU_CORSA))
    // Accovacciarsi segue la clip additiva (la stessa curva della visuale),
    // cosi' corpo, gambe e occhi scendono e risalgono insieme.
    const giuQuanto = (azGiu && curvaGiu.current)
      ? THREE.MathUtils.clamp(campiona(curvaGiu.current, azGiu.time), 0, 1) : 0
    const pesoFam = {
      giu: giuQuanto,
      corre: (1 - giuQuanto) * L.corsa,
      cammina: (1 - giuQuanto) * (1 - L.corsa),
    }

    // Velocita' CALCOLATA, non di stato: con useState il valore letto in
    // questo frame era ancora quello vecchio (setSpeed non è immediato),
    // quindi ogni cambio di andatura partiva con un frame sbagliato.
    // All'indietro e di lato si va un po' piu' piano (VEL_DIR).
    const famAdesso = giu ? 'giu' : shift ? 'corre' : 'cammina'
    const fattoreDir = somma > 1e-3
      ? (Math.max(my, 0) * VEL_DIR.avanti + Math.max(-my, 0) * VEL_DIR.indietro +
         Math.abs(mx) * VEL_DIR.sinistra) / somma
      : 1
    const velocita = VEL_FAM[famAdesso] * fattoreDir

    // Col polso alzato il corpo si ferma del tutto: era l'animazione di
    // base a far vagare il braccio, e centrare un'icona diventava un tiro
    // al bersaglio mobile. Non serve piu' guardare anche se ci si muove:
    // con l'orologio alzato il movimento e' bloccato a monte.
    //
    // ATTENZIONE: si azzera la VELOCITA', non si ferma l'azione. Uno
    // stop() o un peso 0 toglierebbero la posa di base, e l'additivo del
    // gesto si sommerebbe alla posa di RIPOSO — che in questo modello e'
    // una T-pose a braccia spalancate — mandando il braccio da tutt'altra
    // parte. E' esattamente l'errore gia' fatto una volta.
    // La rampa serve a non inchiodarsi di scatto.
    ritmoBase.current += ((orologioAperto ? 0 : 1) - ritmoBase.current) *
                         (1 - Math.exp(-delta * 6))
    // la rampa esponenziale si avvicina a zero senza mai arrivarci:
    // sotto la soglia si aggancia, così il corpo è fermo davvero
    if (ritmoBase.current < 0.004) ritmoBase.current = 0

    // --- LE CLIP DEL MOVIMENTO ---
    // Il loro tempo NON lo avanza il mixer: lo scrive questo codice, da una
    // fase comune a tutte (timeScale 0). Cosi' due clip mescolate hanno sempre
    // lo stesso piede in aria. La fase avanza alla media, pesata, del ritmo
    // di ciascuna clip, e il ritmo e' quello che tiene il piede piantato a
    // questa velocita' (velocita' vera / velocita' della clip).
    // L'oggetto `actions` di drei va letto QUI, a ogni frame: le sue
    // proprieta' sono getter PIGRI che restituiscono undefined finche' il
    // ref del modello non e' agganciato. Metterlo in un useMemo lo
    // congela a lista vuota al primo render — ed e' successo: il braccio
    // continuava ad animarsi perche' la velocita' non finiva su nessuno.
    const intens = joyActive ? joyMag : 1
    const medie = medieTesta.current
    tmp.attesa.set(0, 0, 0)
    let pesoTot = 0, cicli = 0
    // PIU' CASELLE SUONANO LA STESSA CLIP: Walk fa avanti e indietro (a
    // rovescio), Run_Left il passo laterale di tutte e tre le famiglie. I
    // pesi si sommano sull'azione — una sola per clip, il mixer non ne
    // ammette due — e il TEMPO lo detta la casella che pesa di piu'.
    const pesiAz = {}
    // eslint-disable-next-line no-restricted-syntax
    for (const v of VOCI) {
      const w = pesoFam[v.fam] * L.dir[v.dir]
      // Se il GLB in cache e' vecchio e una clip manca, il peso va su Walk:
      // lasciare l'azione a vuoto mescolerebbe la posa di riposo, che in
      // questo modello e' una T-pose a braccia spalancate.
      const a = actions[v.clip] || actions.Walk
      const clip = a?.getClip()
      if (!clip) continue
      // la prima chiave non sta a 0 ma a 1/30 di secondo (l'esportatore parte
      // dal fotogramma 1): senza saltarla, a ogni giro il passo si ferma un attimo
      let inizio = INIZIO_CLIP.get(clip)
      if (inizio === undefined) {
        inizio = Math.min(...clip.tracks.map((t) => t.times[0]))
        INIZIO_CLIP.set(clip, inizio)
      }
      const dur = clip.duration - inizio
      const passo = (VEL_FAM[v.fam] * VEL_DIR[v.dir] * intens) / v.vel   // ritmo della clip
      cicli += w * passo / dur
      pesoTot += w
      // dove porta la testa questa casella (i pesi sommano a 1)
      if (medie?.[clip.name]) tmp.attesa.addScaledVector(medie[clip.name], w)
      const voce = pesiAz[clip.name] ??= { a, w: 0, wMax: -1, t: 0 }
      voce.w += w
      if (w > voce.wMax) {
        voce.wMax = w
        // verso -1 = CLIP A ROVESCIO: la fase scorre all'indietro a partire
        // dallo stesso istante, quindi il piede in aria resta quello giusto
        // anche mescolandola con una casella che va avanti.
        voce.t = inizio + (((v.verso * L.fase + v.fase) % 1 + 1) % 1) * dur
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const nomeAz in pesiAz) {
      const { a, w, t } = pesiAz[nomeAz]
      if (!a.isRunning()) a.play()
      a.timeScale = 0
      a.time = t
      a.setEffectiveWeight(w * L.moto)
    }
    if (pesoTot > 1e-4) L.fase = (L.fase + delta * ritmoBase.current * cicli / pesoTot) % 1
    const idle = actions.Idle
    if (idle) {
      if (!idle.isRunning()) idle.play()
      idle.timeScale = ritmoBase.current
      idle.setEffectiveWeight(1 - L.moto)
    }
    // L'ACCOVACCIATA ADDITIVA serve solo DA FERMI: muovendosi la piega ce
    // l'ha gia' CrouchWalk, che ora suona in tutte e quattro le direzioni, e
    // sommarla la raddoppierebbe. Da in piedi il peso non fa danno: l'azione
    // e' a tempo 0, cioe' delta identita'.
    if (azGiu) azGiu.setEffectiveWeight(1 - L.moto)
    // DOVE SARA' LA TESTA, senza leggerla: media delle clip in movimento,
    // piu' Idle da fermi, piu' quanto la piega additiva la fa scendere. Niente
    // filtro e niente oscillazione (vedi il blocco delle misure in cima).
    tmp.attesa.multiplyScalar(L.moto)
    if (medie?.Idle) tmp.attesa.addScaledVector(medie.Idle, 1 - L.moto)
    if (medie?.piega) tmp.attesa.addScaledVector(medie.piega, giuQuanto * (1 - L.moto))
    // cadenza del passo della camera: 1 = una camminata normale
    const ritmoCamera = pesoTot > 1e-4
      ? (cicli / pesoTot) / ((1 - L.corsa) * (1 / 1.3333) + L.corsa * ((6 / 6.57) / 0.7917))
      : 1

    // ✅ Calcola la direzione in base alla camera
    // (joystick touch se attivo, altrimenti WASD)
    if (joyActive) {
      tmp.avanti.set(0, 0, touchMove.y)
      tmp.lato.set(-touchMove.x, 0, 0)
    } else {
      tmp.avanti.set(0, 0, Number(backward) - Number(forward))
      tmp.lato.set(Number(left) - Number(right), 0, 0)
    }

    // Yaw della visuale dal QUATERNIONE, non da camera.matrix: la
    // matrice viene ricalcolata durante il RENDER, quindi qui dentro è
    // ancora quella del frame precedente, mentre i PointerLockControls
    // scrivono il quaternione sui mousemove (tra un frame e l'altro) →
    // il corpo ruotava sistematicamente un frame in ritardo sulla
    // visuale, e siccome i mousemove arrivano a raffiche irregolari il
    // ritardo non era costante: da qui gli scatti girandosi.
    // Si legge l'asse +Z (per una camera punta all'INDIETRO), che è
    // esattamente ciò che dava la vecchia matrix.elements[8]/[10].
    tmp.sguardo.set(0, 0, 1).applyQuaternion(state.camera.quaternion)
    // il pitch dall'asse ALL'INDIETRO: la sua y è l'opposto di quella
    // della direzione di vista
    const pitchVista = Math.asin(THREE.MathUtils.clamp(-tmp.sguardo.y, -1, 1))
    // LO YAW SI PRENDE DALL'ASSE DESTRO, NON DA QUELLO DI SGUARDO.
    // Guardando a piombo in basso, lo sguardo è verticale: le sue x e z
    // valgono entrambe cos(pitch), cioè 1e-17, mentre l'errore di
    // arrotondamento sul quaternione è 1e-8. L'atan2 di due numeri
    // sommersi dal rumore restituisce un angolo A CASO, diverso a ogni
    // fotogramma: da lì gli scatti e la visuale che si ritrova altrove.
    // L'asse destro della camera invece resta ORIZZONTALE a qualunque
    // inclinazione (i controls non mettono mai rollio), quindi è lungo 1
    // e dà lo stesso yaw senza mai degenerare.
    tmp.destra.set(1, 0, 0).applyQuaternion(state.camera.quaternion)
    const yawVista = Math.atan2(-tmp.destra.z, tmp.destra.x)

    // --- INQUADRATURA DELL'OROLOGIO ---
    // La posa del braccio è costruita per una visuale ORIZZONTALE: alzando
    // il polso mentre si guarda in alto o in basso, il quadrante finirebbe
    // fuori campo. Si porta quindi l'inclinazione al valore voluto, e alla
    // chiusura si torna a dov'era: chi stava guardando uno scaffale se lo
    // ritrova. Lo YAW non si tocca — il corpo lo segue, quindi l'orologio
    // sta sempre alla stessa altezza qualunque direzione si guardi.
    // Si può scrivere direttamente sul quaternione perché col polso alzato
    // il pointer lock è sganciato: nessun mousemove ci combatte contro, e
    // i PointerLockControls rileggono comunque l'euler dal quaternione.
    // Il ritorno si interrompe appena il giocatore muove la visuale, e in
    // ogni caso non dura oltre 0.8 s. I primi 220 ms di input si ignorano:
    // è la stessa finestra in cui CameraController azzera la sensibilità,
    // per via del primo mousemove spurio che Chrome consegna dopo ogni
    // aggancio del pointer lock.
    if (ripristino.current) {
      const eta = performance.now() - ripristinoDa.current
      // Basta un movimento vero per cedere: la vecchia franchigia di 220 ms
      // valeva più della sordina (150 ms), e in quei 70 ms di scarto mouse
      // e ritorno tiravano davvero in direzioni opposte — con un
      // movimento rapido si vedeva la visuale singhiozzare.
      if (eta > 800 || ultimoInput.current > ripristinoDa.current) {
        ripristino.current = false
      }
    }
    if (orologioAperto || ripristino.current) {
      const mira = orologioAperto ? cfgFaccia.inclinazione : pitchSalvato.current
      const scarto = mira - pitchVista
      if (!orologioAperto && Math.abs(scarto) < 0.002) {
        ripristino.current = false
      } else {
        tmp.eulMira.set(
          pitchVista + scarto * (1 - Math.exp(-delta * 9)), yawVista, 0, 'YXZ'
        )
        state.camera.quaternion.setFromEuler(tmp.eulMira)
      }
    }

    tmp.direzione.subVectors(tmp.avanti, tmp.lato).normalize()
      .multiplyScalar(velocita * (joyActive ? joyMag : 1))
      .applyEuler(tmp.eulYaw.set(0, yawVista, 0))

    // Si conserva la velocità verticale (gravità) invariata
    const velAttuale = body.current.linvel()
    tmp.velocita.set(tmp.direzione.x, velAttuale.y, tmp.direzione.z)
    body.current.setLinvel(tmp.velocita, true)
    // Niente setRotation: il collisore è una capsula, ruotarla non
    // cambia nulla e faceva nascere un Quaternion e un Euler per frame.

    // Movimento "a corpo": respiro da fermo, passo, impatti, inerzia.
    // Va fatto qui perché questo useFrame conosce lo stato del moto e
    // gira DOPO quello di CameraController.
    aggiornaCamera(state.camera, delta, state.clock.elapsedTime, {
      muove: inMovimento,
      corre: shift && inMovimento,   // da giu' non si corre: shift e' gia' spento
      ritmo: ritmoCamera,
      laterale: joyActive ? touchMove.x : Number(right) - Number(left),
    })

    // --- CORPO VISIBILE ---
    // La mesh NON sta più dentro il RigidBody. Lì era mossa dalla
    // fisica (rapier la interpola tra passi fissi a 60 Hz) mentre la
    // camera insegue una media esponenziale della posizione GREZZA del
    // corpo: due catene di smorzamento diverse e non sincronizzate, per
    // cui il corpo "nuotava" rispetto alla visuale — e su uno schermo
    // che non va a 60 Hz esatti il disallineamento cambiava ogni frame.
    // In più la camera ha ora respiro e passo, che la mesh non aveva:
    // la testa oscillava e il torace no.
    // Ora il corpo prende la STESSA trasformazione della camera, scosse
    // comprese: rispetto alla vista è immobile per costruzione, e il
    // movimento glielo dà solo la sua animazione scheletrica.
    // --- IL CORPO RESTA UN POCO INDIETRO QUANDO SI GIRA LA VISUALE ---
    // Insegue lo yaw della vista con una costante di tempo (indipendente
    // dagli FPS), piu' corta camminando, e non si stacca mai oltre lo scarto
    // massimo. Col polso alzato NIENTE ritardo: la posa del braccio e il
    // quadrante sono costruiti per un corpo allineato alla camera, e un
    // orologio fuori asse sarebbe difficile da leggere e da cliccare.
    // IL CORPO GIRA SOLO QUANDO LA TESTA LO TIRA DAVVERO. Prima inseguiva
    // sempre, con una costante di tempo: qualunque movimento del mouse, anche
    // di due gradi, portava dietro le spalle — che e' un pupazzo su un perno,
    // non una persona. Ora sotto la soglia il corpo NON SI MUOVE; oltre, si
    // riallinea tutto e poi si rimette fermo, come chi sposta i piedi.
    // Camminando la soglia si annulla (moltiplicata per 1 - moto): le spalle
    // devono seguire la marcia, o si cammina di sbieco.
    if (yawCorpo.current === null) yawCorpo.current = yawVista
    const tau = cfgCorpo.ritardoCorpo * (inMovimento ? cfgCorpo.ritardoCorpoMoto : 1)
    // scarto nel verso piu' corto: da 179 a -179 gradi sono 2, non 358
    let scarto = Math.atan2(Math.sin(yawVista - yawCorpo.current), Math.cos(yawVista - yawCorpo.current))
    if (orologioAperto || tau <= 0) {
      // col polso alzato niente ritardo: la posa del braccio e il quadrante
      // sono costruiti per un corpo allineato alla camera
      yawCorpo.current = yawVista
      giraCorpo.current = false
    } else {
      const soglia = THREE.MathUtils.degToRad(cfgCorpo.sogliaCorpo) * (1 - L.moto)
      if (Math.abs(scarto) > soglia) giraCorpo.current = true
      if (giraCorpo.current) {
        scarto *= Math.exp(-delta / tau)
        // sotto il grado si considera finito, o l'esponenziale non ci arriva mai
        if (Math.abs(scarto) < 0.017) { scarto = 0; giraCorpo.current = false }
        yawCorpo.current = yawVista - scarto
      }
    }
    // --- IL CORPO STA SULLA CAPSULA, NON PIU' APPESO ALLA CAMERA ---
    // Prima la mesh pendeva dalla camera (ne ereditava posizione, rotazione e
    // scosse) e il suo posto veniva poi corretto perche' la testa finisse
    // sotto l'occhio: la testa dipendeva da dove si era messa la camera, che
    // dipendeva da dov'era la testa. Ora la catena va in un verso solo —
    // fisica, corpo, osso Head, occhio — e l'anello non c'e' piu'.
    // Il lerp smorza i passi fissi a 60 Hz di rapier: e' lo stesso di prima,
    // solo spostato dalla camera al corpo. E siccome l'occhio ne DISCENDE,
    // i due non possono piu' sfasarsi (era la causa del corpo che "nuotava").
    const pos = body.current.translation()
    tmp.bersaglio.set(pos.x, pos.y + cfgCorpo.altezza, pos.z)
    if (corpoPosato.current) {
      corpo.current.position.lerp(tmp.bersaglio, 1 - Math.exp(-delta * 30))
    } else {
      corpo.current.position.copy(tmp.bersaglio)   // niente scivolata dall'origine al primo frame
      corpoPosato.current = true
    }
    corpo.current.quaternion.setFromAxisAngle(SU, yawCorpo.current)

    // --- DOV'E' LA TESTA --- gia' calcolata sopra, senza leggerla: e' la
    // media pesata delle clip che stanno suonando. Non serve piu' ne'
    // aggiornare le matrici mondo dell'osso ne' filtrare niente.
    const testa = medie ? tmp.attesa : null

    // IN ORIZZONTALE SI MUOVE IL CORPO: la testa va sotto l'occhio (che sta
    // sull'asse della capsula) e gli resta dietro di `occhioDietro`. In
    // verticale il modello non si tocca MAI: i piedi sono a terra per
    // costruzione, e non c'e' piu' niente da ricompensare.
    if (testa) player.current.position.set(-testa.x, 0, cfgCorpo.occhioDietro - testa.z)

    // IN VERTICALE SI MUOVE LA CAMERA: l'occhio sta sempre `occhioSu` sopra
    // la testa, quindi accovacciandosi scende con lei e non serve sapere di
    // quanto (era CALO_GIU, misurata in Blender). Si posiziona il RIG, il
    // gruppo che contiene la camera, non la camera stessa: a lei resta il
    // solo offset delle scosse, cosi' le rotazioni la fanno girare su se'
    // stessa. Se il rig non c'e' ancora si ricade sulla camera.
    const ancora = getRig() || state.camera
    ancora.position.set(
      corpo.current.position.x,
      corpo.current.position.y + (testa ? testa.y : ALTEZZA_TESTA) + cfgCorpo.occhioSu,
      corpo.current.position.z,
    )

    // --- VICINANZA ALLA CASSA ---
    // Distanza orizzontale dal corpo FISICO: l'altezza non conta, si paga
    // stando davanti al bancone. Si scrive sullo store SOLO quando il valore
    // cambia, altrimenti sarebbe un render di React a ogni frame (il set di
    // questo mini-store avvisa gli iscritti anche a valore uguale).
    const vicino = Math.hypot(pos.x - cfgCassa.x, pos.z - cfgCassa.z) < cfgCassa.raggio
    if (vicino !== vicinoCassaStore.get()) vicinoCassaStore.set(vicino)

    // --- QUADRANTE 3D ---
    // Qui si decide solo SE mostrarlo e con quanta opacità: la posa del
    // piano la fa orientaFaccia, una volta sola per alzata. Nessun
    // calcolo di posizione per frame, quindi nessun ritardo possibile:
    // il piano è figlio del nodo dell'orologio e viaggia con lui.
    const mesh = facciaRef.current
    if (mesh && faccia) {
      const az = azioneOrologio.current
      const avanz = az
        ? THREE.MathUtils.clamp(az.time / az.getClip().duration, 0, 1)
        : (watchOpenStore.get() ? 1 : 0)
      if (avanz < 0.03) {
        mesh.visible = false
        orientata.current = false   // si ricalcola alla prossima alzata
      } else {
        mesh.visible = true
        mesh.material.opacity = THREE.MathUtils.smoothstep(avanz, 0.55, 0.95)
        // si riorienta anche se si muove il cursore del riempimento
        if (avanz > 0.9 &&
            (!orientata.current || mesh.userData.riemp !== cfgFaccia.riempimento)) {
          orientaFaccia(mesh, faccia, state.camera)
          orientata.current = true
          // il canvas deve riprendere la FORMA del piano appena orientato
          cfgFaccia.ridisegna?.()
        }
      }
    }
  })

  return <>
    <RigidBody
      ref={ body }
      colliders={ false }
      position={[0, 1, 0]}
    >
      <CapsuleCollider args={[1.1, 0.5]} position={[0, 0.7, 0]} />
    </RigidBody>
    {/* Corpo visibile: agganciato alla CAPSULA (vedi "L'OCCHIO STA SULLA
        TESTA" in cima al file). La sua posa dentro il gruppo la scrive il
        useFrame: verticale mai, orizzontale quel tanto che serve perche' la
        testa resti sotto l'occhio.
        userData.noRay → ignorato dal mirino (ProductRaycaster) */}
    {/* La UI del quadrante è FIGLIA del nodo WatchScreen: eredita la sua
        matrice, quindi non può restare indietro di un frame né scivolare
        quando il polso si inclina. */}
    {faccia && createPortal(<OrologioFaccia ref={ facciaRef } />, faccia.nodo)}
    <group ref={ corpo }>
      <primitive
        ref={ player }
        object={ scene }
        userData={{ noRay: true }}
        position={[0, 0, 0]}
        rotation={[0, -Math.PI / 2, 0]}
      />
    </group>
  </>
}
